"use client";

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";

import { highlightCodeToTokens, type TokenLine } from "@/lib/code/highlighter";
import { resolveLanguageId } from "@/lib/code/language";
import { cn } from "@/lib/utils";

export type CodeLineHighlight = {
  from: number;
  to?: number;
  className?: string;
  label?: string;
};

export type CodeBlockProps = {
  code: string;
  language?: string;
  wrap?: boolean;
  lineNumbers?: boolean;
  startingLineNumber?: number;
  maxHeight?: number | string;
  lineHighlights?: CodeLineHighlight[];
  className?: string;
};

type HighlightMeta = {
  className?: string;
  label?: string;
};

const DEFAULT_MAX_HEIGHT = 480;

type HighlightState = {
  lang: string;
  tokens: TokenLine[];
};

export function CodeBlock({
  code,
  language,
  wrap = false,
  lineNumbers = true,
  startingLineNumber = 1,
  maxHeight = DEFAULT_MAX_HEIGHT,
  lineHighlights,
  className,
}: CodeBlockProps) {
  const normalizedLang = resolveLanguageId(language);
  const highlightLang = process.env.NODE_ENV === "test" ? undefined : normalizedLang;
  const [tokenState, setTokenState] = useState<HighlightState | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!highlightLang) {
      return () => {
        cancelled = true;
      };
    }

    highlightCodeToTokens(code, highlightLang)
      .then((tokens) => {
        if (!cancelled) {
          setTokenState({ lang: highlightLang, tokens: tokens ?? [] });
        }
      })
      .catch(() => {
        if (!cancelled) {
            setTokenState(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [code, highlightLang]);

  const plainLines = useMemo(() => code.split(/\r?\n/u), [code]);
  const activeTokens =
    highlightLang && tokenState && tokenState.lang === highlightLang ? tokenState.tokens : null;
  const isTokenMode = Boolean(activeTokens);
  const linesToRender = (activeTokens ?? plainLines) as (TokenLine | string)[];

  const highlightMap = useMemo(() => {
    if (!lineHighlights?.length) {
      return new Map<number, HighlightMeta>();
    }

    const map = new Map<number, HighlightMeta>();

    for (const highlight of lineHighlights) {
      const from = Math.max(1, Math.floor(highlight.from));
      const to = Math.max(from, Math.floor(highlight.to ?? highlight.from));

      for (let line = from; line <= to; line += 1) {
        const existing = map.get(line);
        const next: HighlightMeta = {
          className: cn(existing?.className, highlight.className),
          label: highlight.label ?? existing?.label,
        };
        map.set(line, next);
      }
    }

    return map;
  }, [lineHighlights]);

  const safeStart = Math.max(1, Math.floor(startingLineNumber));
  const totalLines = Math.max(1, linesToRender.length);
  const gutterDigits = Math.max(2, String(safeStart + totalLines - 1).length);
  const gutterWidth = `calc(${gutterDigits}ch + 0.75rem)`;

  const scrollMaxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
  const lineContentClass = wrap ? "whitespace-pre-wrap break-words" : "whitespace-pre";

  return (
    <div
      className={cn(
        "relative w-full max-w-full min-w-0 overflow-hidden rounded-[var(--radius-xl)] border border-border/60 bg-[color-mix(in_oklab,var(--token-surface-default)_94%,transparent)] font-mono text-sm",
        className,
      )}
    >
      <div
        className="max-h-full w-full max-w-full overflow-auto"
        style={{ maxHeight: scrollMaxHeight }}
      >
        <div className="grid gap-1 px-4 py-4 text-[13px] leading-6">
          {linesToRender.map((line, index) => {
            const lineNumber = safeStart + index;
            const highlight = highlightMap.get(lineNumber);
            const tokenLine = isTokenMode ? (line as TokenLine) : null;
            const plainLine = isTokenMode ? "" : (line as string);

            return (
              <div
                key={`code-line-${lineNumber}`}
                className={cn(
                  "flex min-h-[1.25rem] items-start gap-4 rounded-sm border-l-2 border-transparent px-2 py-0.5",
                  highlight?.className,
                )}
                data-line-number={lineNumber}
                data-line-label={highlight?.label}
              >
                {lineNumbers ? (
                  <div
                    className="pointer-events-none select-none text-right text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70"
                    aria-hidden="true"
                    style={{ width: gutterWidth }}
                  >
                    {lineNumber}
                  </div>
                ) : null}
                <div className={cn("flex-1 text-foreground", lineContentClass)}>
                  {isTokenMode && tokenLine
                    ? renderTokens(tokenLine)
                    : plainLine || "\u00A0"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function renderTokens(tokens: TokenLine) {
  if (!tokens.length) {
    return (
      <span key="token-empty">{"\u00A0"}</span>
    );
  }

  return tokens.map((token, index) => (
    <span key={`token-${index}`} style={getTokenStyle(token)}>
      {token.content || "\u00A0"}
    </span>
  ));
}

function getTokenStyle(token: TokenLine[number]): CSSProperties {
  const style: CSSProperties = {};
  if (token.color) {
    style.color = token.color;
  }

  const fontStyle = token.fontStyle ?? 0;
  if (fontStyle & 1) {
    style.fontStyle = "italic";
  }
  if (fontStyle & 2) {
    style.fontWeight = 600;
  }
  if (fontStyle & 4) {
    style.textDecoration = "underline";
  }

  return style;
}
